import './action-menu.scss';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Avatar,
  ButtonProps,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import React from 'react';
import styled from 'styled-components';
import { useTracking } from '@act/react-components/tracking';

interface VerticallyAlignCenterDivProps {
  $verticallyAlignCenter: boolean;
}
const VerticallyAlignCenterDiv = styled.div<VerticallyAlignCenterDivProps>`
  margin: ${$verticallyAlignCenter =>
    $verticallyAlignCenter ? 'margin: calc(50% - 5px) 8px 0 0' : ''};
`;

type HorizontalAlignment = 'left' | 'right';

export interface Action {
  /**
   * What should the action button say?
   */
  display: string;
  /**
   * Is this menu item available?
   */
  disabled?: boolean;
  /**
   * Optional click handler
   */
  callback?: () => void;
  /**
   * Canonical Datadog tracking name for this action.
   */
  trackingName?: string;
  /**
   * Optional prop to display the action as disabled
   */
  displayAsDisabled?: boolean;
}

export interface ActionMenuProps {
  /**
   * What are the action items?
   */
  actions: Action[];
  /**
   * Secondary menu action items appear after a divider
   */
  secondaryActions?: Action[];
  /**
   * How should the pop up menu align to the three dots?
   */
  horizontalAlign?: HorizontalAlignment;
  /**
   * Should we center the action items?
   */
  verticallyAlignCenter?: boolean;
  color?: ButtonProps['color']; // Use a more useful type
  borderColor?: string;
  /**
   * Tracking name for the menu trigger icon button.
   */
  trackingName?: string;
}

/**
 * Action menu component for displaying a list of app functions
 * ```import { ActionMenu } from '@act/storybook';```
 */
export const ActionMenu = ({
  actions,
  secondaryActions,
  horizontalAlign = 'left',
  verticallyAlignCenter = false,
  color,
  borderColor,
  trackingName = 'action-menu-trigger',
}: ActionMenuProps) => {
  const { generateTrackingProps } = useTracking();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleClick = callback => {
    handleClose();
    if (callback) callback();
  };
  const existsEnabledAction = !!actions.find(
    a => !a.disabled && !a.displayAsDisabled
  );
  const existsEnabledSecondaryAction =
    secondaryActions &&
    !!secondaryActions.find(sa => !sa.disabled && !sa.displayAsDisabled);

  return (
    <VerticallyAlignCenterDiv $verticallyAlignCenter={verticallyAlignCenter}>
      {(existsEnabledAction || existsEnabledSecondaryAction) && (
        <Avatar
          sx={{
            backgroundColor: 'transparent',
            border: borderColor ? `1px solid ${borderColor}` : '',
          }}
        >
          <IconButton
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleOpenMenu}
            color={color}
            {...generateTrackingProps(trackingName)}
          >
            <MoreVertIcon />
          </IconButton>
        </Avatar>
      )}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={
          horizontalAlign === 'right'
            ? { vertical: 'top', horizontal: 'right' }
            : { vertical: 'top', horizontal: 'left' }
        }
        anchorOrigin={
          horizontalAlign === 'right'
            ? { vertical: 'bottom', horizontal: 'right' }
            : { vertical: 'bottom', horizontal: 'left' }
        }
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {actions.map(a => (
          <MenuItem
            className={
              a.displayAsDisabled ? 'action-item disabled' : 'action-item'
            }
            onClick={() => handleClick(a.callback)}
            key={a.display}
            disabled={a.disabled || false}
            {...(a.trackingName ? generateTrackingProps(a.trackingName) : {})}
          >
            {a.display}
          </MenuItem>
        ))}
        {secondaryActions?.length && <Divider />}
        {secondaryActions?.length &&
          secondaryActions.map(a => (
            <MenuItem
              onClick={() => handleClick(a.callback)}
              key={a.display}
              disabled={a.disabled || false}
              {...(a.trackingName ? generateTrackingProps(a.trackingName) : {})}
            >
              {a.display}
            </MenuItem>
          ))}
      </Menu>
    </VerticallyAlignCenterDiv>
  );
};
